import { PureComponent, ReactNode } from 'react';
import moment, { Moment } from 'moment';
import { RouteComponentProps } from 'react-router-dom';
import { Helmet } from 'react-helmet';

import {
    TopElementsPtByDateResponseType,
    fetchTopCounts,
    Category,
    fetchItemsCounts,
    fetchPatientsList,
} from '../api/fetchAnalytics';

import NavBar from '../components/common/NavBar';
import { GraphDataType } from '../components/analytics/GraphView';
import { AnalyticsDashboardQueryParams, GO_TO_ANALYTICS_DASHBOARD } from '../routes/paths';
import { track } from '../tracking';
import { captureError, captureErrorString } from './captureError';
import { Menu, Radio, notification } from 'antd';
import { RangeValue } from '../components/common/DatePicker';
import getDoctorAnalyticsGuestToken, {
    DOCTOR_ANALYTICS_GUEST_TOKEN_TYPE,
} from '../api/getDoctorAnalyticsGuestToken';
import { embedDashboard } from '@superset-ui/embedded-sdk';
import NewHeaderComponent from '../components/common/NewHeaderComponent';
import DateFilter from '../components/headerfilters/DateFilter';
import { amIBusinessUser, amIDoctor } from '../store/idbHandler';
import { his } from './history';
import { getAclViewClassName } from '../restrictions/utils';
import { SREV2 } from '../api/pushGenie';

export type TimeFilterType =
    | 'yesterday'
    | 'last-7-days'
    | 'current-month'
    | 'previous-month'
    | 'last-3-months';

const YESTERDAY = 'yesterday';
const LAST_7_DAYS = 'last-7-days';
const CURRENT_MONTH = 'current-month';
const PREVIOUS_MONTH = 'previous-month';
const LAST_3_MONTHS = 'last-3-months';

export type FetchState = 'loading' | 'error' | 'success';

export type PatientAnalyticsStateType = {
    [k in PatientAnalyticsFilterKeys]: {
        fetchState: FetchState;
        count?: string | number;
    };
};

export type AnalyticsFiltersStateType = {
    [k in AnalyticsFiltersKeys]: {
        fetchState: FetchState;
        info?: number | string;
        count?: number | string;
    };
};

export type SyDyMxLbStateType = {
    fetchState: FetchState;
    data?: Data[];
};

export interface Data {
    name: string;
    count: string;
    id: string;
    eid: string;
}

type TabType = 'graph' | 'list';
type State = {
    patientAnalytics: PatientAnalyticsStateType;
    syDyMxLbAnalytics: SyDyMxLbStateType;
    analyticsFilters: AnalyticsFiltersStateType;
    infoContainer: {
        fetchState: FetchState;
        data?: GraphDataType | { age: string; gen: string; name: string; oid: string }[];
        data2?: GraphDataType | { age: string; gen: string; name: string; oid: string }[];
    };
    infoContainerFilter: string;
    selectedInfoContainerView: TabType;
    compareWith?: 'week' | 'month';
    currTimeFilter: string;
    dates: RangeValue<Moment> | undefined;
    analyticsGuestTokenType: DOCTOR_ANALYTICS_GUEST_TOKEN_TYPE;
};

type Props = RouteComponentProps;

export type PatientAnalyticsFilterKeys = 'total' | 'male' | 'female' | 'new-patients';

enum PatientAnalyticsFiltersEnum {
    TOTAL_PATIENTS = 'total',
    MALE_PATIENTS = 'male',
    FEMALE_PATIENTS = 'female',
    NEW_PATIENTS = 'new-patients',
}

export const VALID_PATIENT_VISIT_ANALYTICS: PatientAnalyticsFilterKeys[] = [
    PatientAnalyticsFiltersEnum.TOTAL_PATIENTS,
    PatientAnalyticsFiltersEnum.MALE_PATIENTS,
    PatientAnalyticsFiltersEnum.FEMALE_PATIENTS,
    PatientAnalyticsFiltersEnum.NEW_PATIENTS,
];

enum AnalyticsFiltersEnum {
    TOTAL_PATIENTS = 'total-patients',
    TOP_SYMPTOMS = 'top-symptoms',
    TOP_DIAGNOSIS = 'top-diagnosis',
    TOP_MEDICATIONS = 'top-medications',
    TOP_LABTESTS = 'top-labtests',
    TOP_ACTION_CLASS = 'top-action-class',
    TOP_COMPANIES = 'top-companies',
    TOTAL_APPT = 'total-appointments',
}

const analyticsFilterWithoutPatientList = [
    AnalyticsFiltersEnum.TOTAL_APPT,
    AnalyticsFiltersEnum.TOP_ACTION_CLASS,
    AnalyticsFiltersEnum.TOP_COMPANIES,
    AnalyticsFiltersEnum.TOTAL_PATIENTS,
];

const analyticsFiltersInitialState: AnalyticsFiltersStateType = {
    [AnalyticsFiltersEnum.TOTAL_PATIENTS]: {
        fetchState: 'loading',
    },
    [AnalyticsFiltersEnum.TOP_SYMPTOMS]: {
        fetchState: 'loading',
    },
    [AnalyticsFiltersEnum.TOP_DIAGNOSIS]: {
        fetchState: 'loading',
    },
    [AnalyticsFiltersEnum.TOP_MEDICATIONS]: {
        fetchState: 'loading',
    },
    [AnalyticsFiltersEnum.TOP_LABTESTS]: {
        fetchState: 'loading',
    },
    [AnalyticsFiltersEnum.TOP_ACTION_CLASS]: {
        fetchState: 'loading',
    },
    [AnalyticsFiltersEnum.TOP_COMPANIES]: {
        fetchState: 'loading',
    },
    [AnalyticsFiltersEnum.TOTAL_APPT]: {
        fetchState: 'loading',
    },
};

export type AnalyticsFiltersKeys =
    | 'total-patients'
    | 'top-symptoms'
    | 'top-diagnosis'
    | 'top-medications'
    | 'top-labtests'
    | 'top-action-class'
    | 'top-companies'
    | 'total-appointments';

export const VALID_SY_DY_MX_LB_ANALYTICS: Set<AnalyticsFiltersKeys> = new Set([
    AnalyticsFiltersEnum.TOP_SYMPTOMS,
    AnalyticsFiltersEnum.TOP_DIAGNOSIS,
    AnalyticsFiltersEnum.TOP_MEDICATIONS,
    AnalyticsFiltersEnum.TOP_LABTESTS,
    AnalyticsFiltersEnum.TOP_ACTION_CLASS,
    AnalyticsFiltersEnum.TOP_COMPANIES,
    AnalyticsFiltersEnum.TOTAL_APPT,
]);

export const PATIENT_VISITS_FILTERS: {
    id: PatientAnalyticsFilterKeys;
    label: string;
    icon: ReactNode;
}[] = [
    {
        id: PatientAnalyticsFiltersEnum.TOTAL_PATIENTS,
        label: 'Total Patients',
        icon: (
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512">
                <path d="M96 224a64 64 0 1 0-64-64 64.06 64.06 0 0 0 64 64zm224 32a112 112 0 1 0-112-112 111.94 111.94 0 0 0 112 112zm32 96c0-19.1 3.92-37.17 10.09-54.17A152.75 152.75 0 0 1 320 304c-24.6 0-47.6-6-68.5-16h-8.3A115.23 115.23 0 0 0 128 403.2V432a48 48 0 0 0 48 48h241c-39.22-29.19-65-75.47-65-128zm-178.9-77.4A63.81 63.81 0 0 0 128 256H64a64.06 64.06 0 0 0-64 64v32a32 32 0 0 0 32 32h65.9a146.64 146.64 0 0 1 75.2-109.4zM512 224a128 128 0 1 0 128 128 128 128 0 0 0-128-128zm64 144a5.33 5.33 0 0 1-5.33 5.33h-37.34v37.34A5.33 5.33 0 0 1 528 416h-32a5.33 5.33 0 0 1-5.33-5.33v-37.34h-37.34A5.33 5.33 0 0 1 448 368v-32a5.33 5.33 0 0 1 5.33-5.33h37.34v-37.34A5.33 5.33 0 0 1 496 288h32a5.33 5.33 0 0 1 5.33 5.33v37.34h37.34A5.33 5.33 0 0 1 576 336z"></path>
            </svg>
        ),
    },
    {
        id: PatientAnalyticsFiltersEnum.MALE_PATIENTS,
        label: 'Male Patients',
        icon: (
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 192 512">
                <path d="M96 0c35.346 0 64 28.654 64 64s-28.654 64-64 64-64-28.654-64-64S60.654 0 96 0m48 144h-11.36c-22.711 10.443-49.59 10.894-73.28 0H48c-26.51 0-48 21.49-48 48v136c0 13.255 10.745 24 24 24h16v136c0 13.255 10.745 24 24 24h64c13.255 0 24-10.745 24-24V352h16c13.255 0 24-10.745 24-24V192c0-26.51-21.49-48-48-48z" />
            </svg>
        ),
    },
    {
        id: PatientAnalyticsFiltersEnum.FEMALE_PATIENTS,
        label: 'Female Patients',
        icon: (
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 512">
                <path d="M128 0c35.346 0 64 28.654 64 64s-28.654 64-64 64c-35.346 0-64-28.654-64-64S92.654 0 128 0m119.283 354.179l-48-192A24 24 0 0 0 176 144h-11.36c-22.711 10.443-49.59 10.894-73.28 0H80a24 24 0 0 0-23.283 18.179l-48 192C4.935 369.305 16.383 384 32 384h56v104c0 13.255 10.745 24 24 24h32c13.255 0 24-10.745 24-24V384h56c15.591 0 27.071-14.671 23.283-29.821z" />
            </svg>
        ),
    },
    // 'other-patients': {
    //     id: 'other-patients',
    //     label: 'Other Patients',
    //     icon: (
    //         <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512">
    //             <path d="M372 0h-79c-10.7 0-16 12.9-8.5 20.5l16.9 16.9-80.7 80.7C198.5 104.1 172.2 96 144 96 64.5 96 0 160.5 0 240c0 68.5 47.9 125.9 112 140.4V408H76c-6.6 0-12 5.4-12 12v40c0 6.6 5.4 12 12 12h36v28c0 6.6 5.4 12 12 12h40c6.6 0 12-5.4 12-12v-28h36c6.6 0 12-5.4 12-12v-40c0-6.6-5.4-12-12-12h-36v-27.6c64.1-14.6 112-71.9 112-140.4 0-28.2-8.1-54.5-22.1-76.7l80.7-80.7 16.9 16.9c7.6 7.6 20.5 2.2 20.5-8.5V12c0-6.6-5.4-12-12-12zM144 320c-44.1 0-80-35.9-80-80s35.9-80 80-80 80 35.9 80 80-35.9 80-80 80z" />
    //         </svg>
    //     ),
    // },
    // {
    //     id: PatientAnalyticsFiltersEnum.NEW_PATIENTS,
    //     label: 'New Patients',
    //     icon: (
    //         <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512">
    //             <path d="M624 208h-64v-64c0-8.8-7.2-16-16-16h-32c-8.8 0-16 7.2-16 16v64h-64c-8.8 0-16 7.2-16 16v32c0 8.8 7.2 16 16 16h64v64c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16v-64h64c8.8 0 16-7.2 16-16v-32c0-8.8-7.2-16-16-16zm-400 48c70.7 0 128-57.3 128-128S294.7 0 224 0 96 57.3 96 128s57.3 128 128 128zm89.6 32h-16.7c-22.2 10.2-46.9 16-72.9 16s-50.6-5.8-72.9-16h-16.7C60.2 288 0 348.2 0 422.4V464c0 26.5 21.5 48 48 48h352c26.5 0 48-21.5 48-48v-41.6c0-74.2-60.2-134.4-134.4-134.4z" />
    //         </svg>
    //     ),
    // },
];

export const ANALYTICS_FILTERS: {
    id: AnalyticsFiltersKeys;
    heading: string;
    metaInfo?: string;
    unit?: string;
}[] = [
    {
        id: AnalyticsFiltersEnum.TOTAL_APPT as AnalyticsFiltersKeys,
        heading: 'Total Appointments',
        metaInfo: 'Total',
    },
    {
        id: AnalyticsFiltersEnum.TOP_SYMPTOMS as AnalyticsFiltersKeys,
        heading: 'Top Symptoms',
        unit: 'times',
    },
    {
        id: AnalyticsFiltersEnum.TOP_DIAGNOSIS as AnalyticsFiltersKeys,
        heading: 'Top Diagnosis',
        unit: 'times',
    },
    {
        id: AnalyticsFiltersEnum.TOP_MEDICATIONS as AnalyticsFiltersKeys,
        heading: 'Top Drugs',
        unit: 'times',
    },
    {
        id: AnalyticsFiltersEnum.TOP_LABTESTS as AnalyticsFiltersKeys,
        heading: 'Top Investigations',
        unit: 'times',
    },
    {
        id: AnalyticsFiltersEnum.TOP_ACTION_CLASS as AnalyticsFiltersKeys,
        heading: 'Top Action Classes',
        unit: 'times',
    },
    {
        id: AnalyticsFiltersEnum.TOP_COMPANIES as AnalyticsFiltersKeys,
        heading: 'Top Companies',
        unit: 'times',
    },
];

export const timeFiltersObj: {
    [k in TimeFilterType]: {
        id: TimeFilterType;
        label: string;
        startDate: string;
        endDate: string;
    };
} = {
    [YESTERDAY]: {
        id: YESTERDAY,
        label: 'Yesterday',
        startDate: moment().subtract(1, 'day').format('YYYY-MM-DD'),
        endDate: moment().subtract(1, 'day').format('YYYY-MM-DD'),
    },
    [LAST_7_DAYS]: {
        id: LAST_7_DAYS,
        label: 'Last 7 Days',
        startDate: moment().subtract(7, 'day').format('YYYY-MM-DD'),
        endDate: moment().subtract(1, 'day').format('YYYY-MM-DD'),
    },
    [CURRENT_MONTH]: {
        id: CURRENT_MONTH,
        label: 'Current Month',
        startDate: moment().startOf('month').format('YYYY-MM-DD'),
        endDate: moment().format('YYYY-MM-DD'),
    },
    [PREVIOUS_MONTH]: {
        id: PREVIOUS_MONTH,
        label: 'Previous Month',
        startDate: moment().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
        endDate: moment().subtract(1, 'month').endOf('month').format('YYYY-MM-DD'),
    },
    [LAST_3_MONTHS]: {
        id: LAST_3_MONTHS,
        label: 'Last 3 Months',
        startDate: moment().subtract(3, 'month').format('YYYY-MM-DD'),
        endDate: moment().subtract(1, 'day').format('YYYY-MM-DD'),
    },
};

class AnalyticsDashboard extends PureComponent<Props, State> {
    initialState: State = {
        patientAnalytics: {
            [PatientAnalyticsFiltersEnum.TOTAL_PATIENTS]: {
                fetchState: 'loading',
            },
            [PatientAnalyticsFiltersEnum.MALE_PATIENTS]: {
                fetchState: 'loading',
            },
            [PatientAnalyticsFiltersEnum.FEMALE_PATIENTS]: {
                fetchState: 'loading',
            },
            [PatientAnalyticsFiltersEnum.NEW_PATIENTS]: { fetchState: 'loading' },
        },
        syDyMxLbAnalytics: {
            fetchState: 'loading',
            data: [],
        },
        analyticsFilters: analyticsFiltersInitialState,
        infoContainer: { fetchState: 'loading' },
        infoContainerFilter: '',
        selectedInfoContainerView: 'graph',
        currTimeFilter: timeFiltersObj['last-7-days'].label,
        dates: undefined,
        analyticsGuestTokenType: DOCTOR_ANALYTICS_GUEST_TOKEN_TYPE.ANALYTICS_DASHBOARD,
    };

    state: State = this.initialState;

    componentDidMount = () => {
        const { startDate, endDate } = timeFiltersObj['last-7-days'];
        const analyticsGuestTokenType = amIDoctor()
            ? DOCTOR_ANALYTICS_GUEST_TOKEN_TYPE.ANALYTICS_DASHBOARD
            : amIBusinessUser()
            ? DOCTOR_ANALYTICS_GUEST_TOKEN_TYPE.FD_ANALYTICS_DASHBOARD
            : '';
        if (!analyticsGuestTokenType) {
            his.goBack();
            captureErrorString('Analytics authentication token type is not supported');
            return;
        }
        this.onMount(analyticsGuestTokenType, startDate, endDate);
        this.setState({ analyticsGuestTokenType });
    };

    onMount = async (
        analyticsGuestTokenType: DOCTOR_ANALYTICS_GUEST_TOKEN_TYPE,
        startDate: string,
        endDate: string,
    ) => {
        const start = startDate.slice(0, 10);
        const end = endDate.slice(0, 10);
        console.log({
            start: start,
            end: end,
        });
        try {
            const { domain, embedded_id, guest_token } = await getDoctorAnalyticsGuestToken(
                analyticsGuestTokenType,
            );
            if (domain && embedded_id && guest_token) {
                embedDashboard({
                    supersetDomain: domain,
                    id: embedded_id,
                    mountPoint: document.getElementById('my-superset-container') as HTMLElement,
                    fetchGuestToken: async (): Promise<string> => guest_token,
                    dashboardUiConfig: {
                        hideTitle: true,
                        urlParams: {
                            start: start,
                            end: end,
                        },
                        filters: {
                            visible: false,
                            expanded: false,
                        },
                    },
                });
                track('GET_DOCTOR_ANALYTICS_GUEST_TOKEN', {
                    domain,
                    embedded_id,
                    guest_token,
                });
            } else {
                captureErrorString('Failed to get analytics guest token');
                track('ERROR', {
                    on: 'getDoctorAnalyticsGuestToken',
                    error: 'Failed to get analytics guest token',
                });
                notification.error({
                    message: 'Failed to get analytics',
                });
            }
        } catch (error) {
            captureError(error as Error);
            track('ERROR', {
                on: 'getDoctorAnalyticsGuestToken',
                error: (error as Error).message,
            });
            notification.error({
                message: 'Failed to get analytics',
            });
        }
    };

    goToDefaultUrl = (): void => {
        const { startDate, endDate } = timeFiltersObj['last-7-days'];
        return this.props.history.replace(
            GO_TO_ANALYTICS_DASHBOARD(AnalyticsFiltersEnum.TOTAL_APPT, startDate, endDate),
        );
    };

    getAnalyticsFilterFromUrl = (): AnalyticsFiltersKeys => {
        const analyticsFilter = new URLSearchParams(this.props.location.search).get(
            AnalyticsDashboardQueryParams.analytics_filter,
        ) as AnalyticsFiltersKeys;

        return analyticsFilter;
    };

    getAnalyticsData = (): void => {
        this.fetchAndSetTopSyDyMxLb();
    };

    getAnalyticsMetaData = async (): Promise<void> => {
        const currentAnalyticsFilter = this.getAnalyticsFilterFromUrl();

        switch (currentAnalyticsFilter) {
            case AnalyticsFiltersEnum.TOTAL_PATIENTS:
                // this.fetchAndSetNewPatientsCount();
                this.setupInfoContainer();

                break;
            case AnalyticsFiltersEnum.TOP_SYMPTOMS:
                this.fetchAndSetSyDyMxLb('sy', this.setupInfoContainer);
                break;
            case AnalyticsFiltersEnum.TOP_DIAGNOSIS:
                this.fetchAndSetSyDyMxLb('dx', this.setupInfoContainer);
                break;
            case AnalyticsFiltersEnum.TOP_MEDICATIONS:
                this.fetchAndSetSyDyMxLb('drug', this.setupInfoContainer);
                break;
            case AnalyticsFiltersEnum.TOP_LABTESTS:
                this.fetchAndSetSyDyMxLb('lab', this.setupInfoContainer);
                break;
            case AnalyticsFiltersEnum.TOP_ACTION_CLASS:
                this.fetchAndSetSyDyMxLb('ac_class', this.setupInfoContainer);
                break;
            case AnalyticsFiltersEnum.TOP_COMPANIES:
                this.fetchAndSetSyDyMxLb('company', this.setupInfoContainer);
                break;
            case AnalyticsFiltersEnum.TOTAL_APPT:
                this.fetchAndSetSyDyMxLb('appt', this.setupInfoContainer);
                break;
        }
    };

    fetchAndSetPatientsCount = (): void => {
        const { startDate, endDate } = this.getStartDateAndEndDateFromUrl();

        fetchTopCounts({
            s_date: startDate,
            e_date: endDate,
            category: 'patients',
            limit: 1,
        })
            .then((res) => {
                if (res.data) {
                    const totalPtCount = res.data.patients.find((t) => t.name === 'total')
                        ?.rx_count;

                    const malePtCount = res.data.patients.find((t) => t.name === 'male')?.rx_count;

                    const femalePtCount = res.data.patients.find((t) => t.name === 'female')
                        ?.rx_count;

                    this.setState({
                        patientAnalytics: {
                            ...this.state.patientAnalytics,
                            [PatientAnalyticsFiltersEnum.TOTAL_PATIENTS]: {
                                fetchState: 'success',
                                count: totalPtCount,
                            },
                            [PatientAnalyticsFiltersEnum.MALE_PATIENTS]: {
                                fetchState: 'success',
                                count: malePtCount,
                            },
                            [PatientAnalyticsFiltersEnum.FEMALE_PATIENTS]: {
                                fetchState: 'success',
                                count: femalePtCount,
                            },
                        },
                        analyticsFilters: {
                            ...this.state.analyticsFilters,
                            [AnalyticsFiltersEnum.TOTAL_PATIENTS]: {
                                fetchState: 'success',
                                count: totalPtCount,
                            },
                        },
                    });
                    return;
                } else {
                    this.setState({
                        patientAnalytics: {
                            ...this.state.patientAnalytics,
                            [PatientAnalyticsFiltersEnum.TOTAL_PATIENTS]: {
                                fetchState: 'error',
                            },
                            [PatientAnalyticsFiltersEnum.MALE_PATIENTS]: {
                                fetchState: 'error',
                            },
                            [PatientAnalyticsFiltersEnum.FEMALE_PATIENTS]: {
                                fetchState: 'error',
                            },
                            [PatientAnalyticsFiltersEnum.NEW_PATIENTS]: {
                                fetchState: 'error',
                            },
                        },
                        analyticsFilters: {
                            ...this.state.analyticsFilters,
                            [AnalyticsFiltersEnum.TOTAL_PATIENTS]: {
                                fetchState: 'error',
                            },
                        },
                    });
                }
            })
            .catch((err) => {
                captureErrorString((err as Error).message);
                this.setState({
                    patientAnalytics: {
                        ...this.state.patientAnalytics,
                        [PatientAnalyticsFiltersEnum.TOTAL_PATIENTS]: {
                            fetchState: 'error',
                        },
                        [PatientAnalyticsFiltersEnum.MALE_PATIENTS]: {
                            fetchState: 'error',
                        },
                        [PatientAnalyticsFiltersEnum.FEMALE_PATIENTS]: {
                            fetchState: 'error',
                        },
                        [PatientAnalyticsFiltersEnum.NEW_PATIENTS]: {
                            fetchState: 'error',
                        },
                    },
                    analyticsFilters: {
                        ...this.state.analyticsFilters,
                        [AnalyticsFiltersEnum.TOTAL_PATIENTS]: {
                            fetchState: 'error',
                        },
                    },
                });
            });
    };

    MAP = {
        // [AnalyticsFiltersEnum['TOTAL_PATIENTS']]: 'total',
        [AnalyticsFiltersEnum['TOP_SYMPTOMS']]: 'sy',
        [AnalyticsFiltersEnum['TOP_DIAGNOSIS']]: 'dx',
        [AnalyticsFiltersEnum['TOP_MEDICATIONS']]: 'drug',
        [AnalyticsFiltersEnum['TOP_LABTESTS']]: 'lab',
        [AnalyticsFiltersEnum['TOP_ACTION_CLASS']]: 'ac_class',
        [AnalyticsFiltersEnum['TOP_COMPANIES']]: 'company',
        [AnalyticsFiltersEnum['TOTAL_APPT']]: 'appt',
    };

    fetchAndSetTopSyDyMxLb = (): void => {
        const { startDate, endDate } = this.getStartDateAndEndDateFromUrl();
        Object.keys(this.MAP).map((category) => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const apiCategory: Category = this.MAP[category] as Category;
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            fetchTopCounts({
                s_date: startDate,
                e_date: endDate,
                category: apiCategory,
                limit: 1,
            })
                .then((res) => {
                    if (res.data) {
                        const t = {
                            fetchState: 'success',
                            info: res.data[apiCategory][0]?.name || '-',
                            count: res.data[apiCategory][0]?.rx_count || '-',
                        };
                        this.setState({
                            analyticsFilters: {
                                ...this.state.analyticsFilters,
                                [category]: t,
                            },
                        });
                        return;
                    } else {
                        this.setState({
                            analyticsFilters: {
                                ...this.state.analyticsFilters,
                                [category]: {
                                    fetchState: 'error',
                                    info: undefined,
                                },
                            },
                        });
                    }
                })
                .catch((err) => {
                    captureErrorString((err as Error).message);
                    this.setState({
                        analyticsFilters: {
                            ...this.state.analyticsFilters,
                            [category]: {
                                fetchState: 'error',
                                info: undefined,
                            },
                        },
                    });
                });
        });
    };

    fetchAndSetSyDyMxLb = (category: Category, callback: () => void): void => {
        const { startDate, endDate } = this.getStartDateAndEndDateFromUrl();
        fetchTopCounts({ s_date: startDate, e_date: endDate, limit: 500, category })
            .then((res) => {
                if (res.data[category]) {
                    const syDyMxLb = res.data[category].map((s) => {
                        return {
                            name: s.name,
                            count: s.rx_count.toString(),
                            id: s.name,
                            eid: s.eka_id,
                        };
                    });
                    const newGraphFilter = syDyMxLb.length > 0 ? syDyMxLb[0].name : '';
                    this.setState(
                        {
                            syDyMxLbAnalytics: {
                                fetchState: 'success',
                                data: syDyMxLb,
                            },
                            compareWith: undefined,
                            infoContainerFilter: newGraphFilter,
                        },
                        () => callback(),
                    );
                    return;
                }
                this.setState({
                    syDyMxLbAnalytics: {
                        fetchState: 'error',
                        data: [],
                    },
                    compareWith: undefined,
                });
            })
            .catch((err) => {
                console.error('fetchTopSymptoms', err);
                captureErrorString((err as Error).message);
            });
    };

    fetchGraphDataForSyDyMxAnalytics = async (
        id: string,
        name: string,
        compareWith?: 'week' | 'month',
    ): Promise<TopElementsPtByDateResponseType> => {
        let { startDate, endDate } = this.getStartDateAndEndDateFromUrl();
        if (compareWith === 'week') {
            startDate = moment(startDate).subtract(1, 'week').format('YYYY-MM-DD');
            endDate = moment(endDate).subtract(1, 'week').format('YYYY-MM-DD');
        } else if (compareWith === 'month') {
            startDate = moment(startDate).subtract(1, 'month').format('YYYY-MM-DD');
            endDate = moment(endDate).subtract(1, 'month').format('YYYY-MM-DD');
        }

        const filter = this.getAnalyticsFilterFromUrl();

        try {
            const res = await fetchItemsCounts({
                s_date: startDate,
                e_date: endDate,
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                category: this.MAP[filter],
                items: id,
            });

            if (res.data) {
                return res.data[name];
            }
        } catch (error) {
            throw Error('Error in fetchGraphDataForSyDyMxAnalytics');
        }

        return [];
    };

    setupGraphView = async (compareWith?: 'week' | 'month'): Promise<void> => {
        if (this.state.infoContainer.fetchState !== 'loading') {
            this.setState({
                infoContainer: { ...this.state.infoContainer, fetchState: 'loading' },
            });
        }

        const currentAnalyticsFilter = this.getAnalyticsFilterFromUrl();

        if (currentAnalyticsFilter === AnalyticsFiltersEnum.TOTAL_PATIENTS) {
            try {
                let { startDate, endDate } = this.getStartDateAndEndDateFromUrl();

                if (compareWith === 'week') {
                    startDate = moment(startDate).subtract(1, 'week').format('YYYY-MM-DD');
                    endDate = moment(endDate).subtract(1, 'week').format('YYYY-MM-DD');
                } else if (compareWith === 'month') {
                    startDate = moment(startDate).subtract(1, 'month').format('YYYY-MM-DD');
                    endDate = moment(endDate).subtract(1, 'month').format('YYYY-MM-DD');
                }

                const { infoContainerFilter } = this.state;

                let itemId = 'total';

                switch (infoContainerFilter) {
                    case PatientAnalyticsFiltersEnum.MALE_PATIENTS: {
                        itemId = 'male';
                        break;
                    }
                    case PatientAnalyticsFiltersEnum.FEMALE_PATIENTS: {
                        itemId = 'female';
                        break;
                    }
                    default:
                        break;
                }

                const res = await fetchItemsCounts({
                    s_date: startDate,
                    e_date: endDate,
                    category: 'patients',
                    items: itemId,
                });

                const graphData: GraphDataType = res.data[itemId].map((i) => {
                    return {
                        x: i.created_at,
                        y: i.rx_count,
                    };
                });
                if (currentAnalyticsFilter === this.getAnalyticsFilterFromUrl()) {
                    if (compareWith) {
                        this.setState({
                            infoContainer: {
                                ...this.state.infoContainer,
                                fetchState: 'success',
                                data2: graphData,
                            },
                        });
                    } else {
                        this.setState({
                            infoContainer: {
                                fetchState: 'success',
                                data: graphData,
                                data2: undefined,
                            },
                            compareWith: undefined,
                        });
                    }
                }
            } catch (err) {
                this.setState({
                    infoContainer: {
                        fetchState: 'error',
                        data: [],
                        data2: undefined,
                    },
                    compareWith: undefined,
                });
            }
        }

        if (VALID_SY_DY_MX_LB_ANALYTICS.has(currentAnalyticsFilter)) {
            const { infoContainerFilter, syDyMxLbAnalytics } = this.state;
            const id =
                syDyMxLbAnalytics.data?.find((i) => i.name === infoContainerFilter)?.eid || '';
            const name =
                syDyMxLbAnalytics.data?.find((i) => i.name === infoContainerFilter)?.name || '';

            try {
                const data = await this.fetchGraphDataForSyDyMxAnalytics(id, name, compareWith);
                const graphData: GraphDataType = data.map((i) => {
                    return {
                        x: i.created_at,
                        y: i.rx_count,
                    };
                });
                if (currentAnalyticsFilter === this.getAnalyticsFilterFromUrl()) {
                    if (compareWith) {
                        this.setState({
                            infoContainer: {
                                ...this.state.infoContainer,
                                fetchState: 'success',
                                data2: graphData,
                            },
                        });
                    } else {
                        this.setState({
                            infoContainer: {
                                fetchState: 'success',
                                data: graphData,
                                data2: undefined,
                            },
                            compareWith: undefined,
                        });
                    }
                }
            } catch (err) {
                this.setState({
                    infoContainer: { fetchState: 'error', data: [] },
                    compareWith: undefined,
                });
            }
        }
    };

    setupListView = async (): Promise<void> => {
        if (this.state.infoContainer.fetchState !== 'loading') {
            this.setState({ infoContainer: { fetchState: 'loading' } });
        }

        const currentAnalyticsFilter = this.getAnalyticsFilterFromUrl();
        const { startDate, endDate } = this.getStartDateAndEndDateFromUrl();
        const { infoContainerFilter, syDyMxLbAnalytics } = this.state;

        const id = syDyMxLbAnalytics.data?.find((i) => i.name === infoContainerFilter)?.eid || '';

        fetchPatientsList({
            s_date: startDate,
            e_date: endDate,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            category: this.MAP[currentAnalyticsFilter],
            items: id || infoContainerFilter,
        })
            .then((res) => {
                this.setState({
                    infoContainer: { fetchState: 'success', data: res?.data?.pt_details },
                    compareWith: undefined,
                });
            })
            .catch((err) => {
                captureErrorString((err as Error).message);
                this.setState({
                    infoContainer: { fetchState: 'error', data: [] },
                    compareWith: undefined,
                });
            });
    };

    setTimeFilter = (startDate: string, endDate: string): void => {
        const {
            startDate: currentStartDate,
            endDate: currentEndDate,
        } = this.getStartDateAndEndDateFromUrl();

        if (moment(startDate).isSame(currentStartDate) && moment(endDate).isSame(currentEndDate)) {
            return;
        }
        const analyticsFilter = this.getAnalyticsFilterFromUrl();

        this.props.history.replace(GO_TO_ANALYTICS_DASHBOARD(analyticsFilter, startDate, endDate));
    };

    setInfoContainerFilter = (filter: string): void => {
        const { selectedInfoContainerView } = this.state;
        if (filter === this.state.infoContainerFilter) {
            return;
        }

        // Callback will get the data for info container from the filter
        this.setState(
            {
                infoContainerFilter: filter,
            },
            () => {
                if (selectedInfoContainerView === 'graph') {
                    this.setupGraphView();
                } else {
                    this.setupListView();
                }
            },
        );
    };

    setupInfoContainer = (): void => {
        const { selectedInfoContainerView } = this.state;

        const currentAnalyticsFilter = this.getAnalyticsFilterFromUrl();

        if (currentAnalyticsFilter === AnalyticsFiltersEnum.TOTAL_PATIENTS) {
            this.setState(
                {
                    infoContainerFilter: PatientAnalyticsFiltersEnum.TOTAL_PATIENTS,
                },
                () => {
                    if (selectedInfoContainerView === 'graph') {
                        this.setupGraphView();
                    } else {
                        this.setupListView();
                    }
                },
            );
        } else {
            if (selectedInfoContainerView === 'graph') {
                this.setupGraphView();
            } else {
                this.setupListView();
            }
        }
    };

    onCompareChange = (v: 'week' | 'month' | undefined): void => {
        this.setState({
            compareWith: v,
        });
        this.setupGraphView(v);
        track('ANALYTICS_COMPARE_CHANGE', { compareWith: v });
        return;
    };

    setAnalyticsFilter = (filter: AnalyticsFiltersKeys): void => {
        const { startDate, endDate } = this.getStartDateAndEndDateFromUrl();
        const currentAnalyticsFilter = this.getAnalyticsFilterFromUrl();
        if (currentAnalyticsFilter === filter) {
            return;
        }

        if (analyticsFilterWithoutPatientList.indexOf(filter as AnalyticsFiltersEnum) > -1) {
            this.setState({
                selectedInfoContainerView: 'graph',
            });
        }

        this.setState({
            syDyMxLbAnalytics: {
                ...this.initialState.syDyMxLbAnalytics,
            },
            infoContainer: {
                ...this.initialState.infoContainer,
            },
        });
        this.props.history.replace(GO_TO_ANALYTICS_DASHBOARD(filter, startDate, endDate));
    };

    showList = (): void => {
        track('LIST_VIEW');
        this.setState({
            selectedInfoContainerView: 'list',
        });
        this.setupListView();
    };

    showGraph = (): void => {
        track('GRAPH_VIEW');
        this.setState({
            selectedInfoContainerView: 'graph',
        });
        this.setupGraphView();
    };

    getStartDateAndEndDateFromUrl = (): { startDate: string; endDate: string } => {
        const startDate =
            new URLSearchParams(this.props.location.search).get(
                AnalyticsDashboardQueryParams.start_date,
            ) || '';

        const endDate =
            new URLSearchParams(this.props.location.search).get(
                AnalyticsDashboardQueryParams.end_date,
            ) || '';

        return { startDate, endDate };
    };

    handleClick = (key: TimeFilterType): void => {
        const { analyticsGuestTokenType } = this.state;
        const { startDate, endDate } = timeFiltersObj[key];
        this.setTimeFilter(startDate, endDate);
        this.setState({
            currTimeFilter: timeFiltersObj[key].label,
            dates: undefined,
        });
        track('ANALYTICS_DASHBOARD_TIME_FILTER_CLICK', { key });
        this.onMount(analyticsGuestTokenType, startDate, endDate);
    };

    daysFilter = (): JSX.Element => (
        <Menu className="w-184 rounded-8 pt-0">
            <div className="font-lato border-b border-darwin-neutral-100 items-center flex gap-8 py-8 px-16 text-13 font-700">
                <svg
                    width="14"
                    height="13"
                    viewBox="0 0 14 13"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M0.5 2.00391C0.5 1.41992 0.957031 0.9375 1.54102 0.9375H12.4336C13.0176 0.9375 13.5 1.41992 13.5 2.00391C13.5 2.25781 13.3984 2.48633 13.2461 2.66406L9.03125 7.86914V11.5C9.03125 11.957 8.65039 12.3125 8.19336 12.3125C8.01562 12.3125 7.83789 12.2617 7.68555 12.1348L5.34961 10.2812C5.0957 10.0781 4.96875 9.79883 4.96875 9.49414V7.86914L0.728516 2.66406C0.576172 2.48633 0.5 2.25781 0.5 2.00391ZM1.87109 2.15625L6.03516 7.25977C6.13672 7.38672 6.1875 7.51367 6.1875 7.64062V9.39258L7.8125 10.6875V7.64062C7.8125 7.51367 7.83789 7.38672 7.93945 7.25977L12.1035 2.15625H1.87109Z"
                        fill="#484848"
                    />
                </svg>
                Filter
            </div>
            {Object.entries(timeFiltersObj).map(([k, v]) => (
                <Menu.Item className="py-8 px-16">
                    <Radio
                        defaultChecked={v.label === timeFiltersObj['last-7-days'].label}
                        value={v.label}
                        onClick={() => this.handleClick(k as TimeFilterType)}
                        checked={v.label === this.state.currTimeFilter}
                        className={`${
                            v.label === this.state.currTimeFilter
                                ? 'filter-radio'
                                : 'add-new-pt-radio'
                        }`}
                    >
                        <p
                            className={`select-none font-lato text-neutral-black text-400 text-13 ${
                                v.label === this.state.currTimeFilter && 'font-700'
                            }`}
                        >
                            {v.label}
                        </p>
                    </Radio>
                </Menu.Item>
            ))}
        </Menu>
    );

    onViewAnalytics = (): void => {
        // const today = moment();
        // const firstDayOfPreviousMonth = today.clone().subtract(1, 'month').startOf('month');
        // const lastDayOfPreviousMonth = firstDayOfPreviousMonth.clone().endOf('month');
        window.open(
            'https://dr-analytics.eka.care/superset/dashboard/15/?standalone=true',
            '_blank',
        );
        track('ON_VIEW_ANALYTICS');
    };

    activeDashboardTabChangeHandler = (
        newAnalyticsGuestTokenType: DOCTOR_ANALYTICS_GUEST_TOKEN_TYPE,
    ) => {
        const { analyticsGuestTokenType } = this.state;
        const { handleClick } = this;
        if (analyticsGuestTokenType === newAnalyticsGuestTokenType) return;
        const newState = {
            analyticsGuestTokenType: newAnalyticsGuestTokenType,
        };
        const callback = () => {
            handleClick('last-7-days');
        };
        this.setState(newState, callback);
        // this.fetchTransactions(startDate, endDate, orderStatusFilter, newTab);
        track('ANALYTICS_DASHBOARD_SWITCH_TAB', {
            analyticsGuestTokenType: newAnalyticsGuestTokenType,
        });
    };

    render(): ReactNode {
        const {
            patientAnalytics,
            infoContainerFilter,
            analyticsFilters,
            syDyMxLbAnalytics,
            infoContainer,
            selectedInfoContainerView,
            compareWith,
            analyticsGuestTokenType,
        } = this.state;
        const currentAnalyticsFilter = this.getAnalyticsFilterFromUrl();

        return (
            <div className="flex flex-col w-full h-full bg-white font-lato">
                <Helmet>
                    <meta charSet="utf-8" />
                    <title>EKA - Analytics</title>
                </Helmet>
                <div className="grid h-full overflow-x-auto overflow-y-hidden grid-cols-queueGrid min-w-840 w-full">
                    <NavBar />
                    <div className="flex flex-col h-full col-span-11 overflow-hidden">
                        <NewHeaderComponent
                            title="Analytics"
                            filterComponent={
                                <div className="w-full flex items-center justify-end space-x-8">
                                    <DateFilter
                                        currTimeFilter={this.state.currTimeFilter}
                                        daysFilter={this.daysFilter}
                                        setTimeFilter={(startDate: string, endDate: string) => {
                                            this.setState({
                                                currTimeFilter: 'Custom',
                                            });
                                            const fSDate = moment(startDate).format('YYYY-MM-DD');
                                            const fEDate = moment(endDate).format('YYYY-MM-DD');
                                            this.setTimeFilter(fSDate, fEDate);
                                            this.onMount(analyticsGuestTokenType, fSDate, fEDate);
                                        }}
                                        noOfDays={Number.MAX_SAFE_INTEGER}
                                        dates={this.state.dates}
                                        setDates={(val) => {
                                            track('ANALYTICS_DASHBOARD_SET_DATES');
                                            this.setState({ dates: val });
                                        }}
                                    />
                                </div>
                            }
                        />
                        {amIDoctor() && amIBusinessUser() && (
                            <div
                                className={`flex pl-16 space-x-4 bg-darwin-neutral_new-50 cursor-pointer text-darwin-neutral-900 select-none border-b border-neutral-7 ${getAclViewClassName(
                                    SREV2.BUSINESS_ANALYTICS,
                                )}`}
                            >
                                <button
                                    className={`h-full flex items-center space-x-8 px-16 pt-10 ${
                                        analyticsGuestTokenType ===
                                        DOCTOR_ANALYTICS_GUEST_TOKEN_TYPE.ANALYTICS_DASHBOARD
                                            ? 'border-b-2 pb-8 border-darwin-accent-bright-blue-800 font-700 text-darwin-accent-bright-blue-800'
                                            : 'font-700 py-10 text-darwin-neutral-900'
                                    }`}
                                    onClick={() =>
                                        this.activeDashboardTabChangeHandler(
                                            DOCTOR_ANALYTICS_GUEST_TOKEN_TYPE.ANALYTICS_DASHBOARD,
                                        )
                                    }
                                >
                                    <p className="whitespace-nowrap">Doctor</p>
                                </button>
                                <button
                                    className={`h-full flex items-center space-x-8 px-16 pt-10 ${
                                        analyticsGuestTokenType ===
                                        DOCTOR_ANALYTICS_GUEST_TOKEN_TYPE.FD_ANALYTICS_DASHBOARD
                                            ? 'border-b-2 pb-8 border-darwin-accent-bright-blue-800 font-700 text-darwin-accent-bright-blue-800'
                                            : 'font-700 py-10 text-darwin-neutral-900'
                                    }`}
                                    onClick={() =>
                                        this.activeDashboardTabChangeHandler(
                                            DOCTOR_ANALYTICS_GUEST_TOKEN_TYPE.FD_ANALYTICS_DASHBOARD,
                                        )
                                    }
                                >
                                    <p className="whitespace-nowrap">Business</p>
                                </button>
                            </div>
                        )}
                        <div className="flex-1 relative" id="my-superset-container">
                            <div className="absolute inset-0 flex flex-col items-center justify-center overflow-hidden">
                                <svg
                                    className="w-24 h-24 fill-darwin-accent-bright-blue-800 animate-spin"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 512 512"
                                >
                                    <path d="M96 256c0-26.5-21.5-48-48-48S0 229.5 0 256s21.5 48 48 48S96 282.5 96 256zM108.9 60.89c-26.5 0-48.01 21.49-48.01 47.99S82.39 156.9 108.9 156.9s47.99-21.51 47.99-48.01S135.4 60.89 108.9 60.89zM108.9 355.1c-26.5 0-48.01 21.51-48.01 48.01S82.39 451.1 108.9 451.1s47.99-21.49 47.99-47.99S135.4 355.1 108.9 355.1zM256 416c-26.5 0-48 21.5-48 48S229.5 512 256 512s48-21.5 48-48S282.5 416 256 416zM464 208C437.5 208 416 229.5 416 256s21.5 48 48 48S512 282.5 512 256S490.5 208 464 208zM403.1 355.1c-26.5 0-47.99 21.51-47.99 48.01S376.6 451.1 403.1 451.1s48.01-21.49 48.01-47.99S429.6 355.1 403.1 355.1zM256 0C229.5 0 208 21.5 208 48S229.5 96 256 96s48-21.5 48-48S282.5 0 256 0z"></path>
                                </svg>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default AnalyticsDashboard;
